// App.tsx
import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { QueryClientProvider } from '@tanstack/react-query';
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
// NOTE: Blueprint CSS still loaded for components not yet migrated (admin, cell editors, dialogs)

// Utils and providers
import { queryClient } from '../utils/constants';
import { MetadataProvider } from '../utils/providers';

// Components
import ErrorBoundary from './ErrorBoundary';
import BuildUpdateWatcher from './BuildUpdateWatcher';
import ProtectedRoute from './ProtectedRoute';
import AuthenticatedApp from './AuthenticatedApp';
import { Login, ResetPassword, Logout } from './Auth';

// Toast notifications
import { Toaster } from 'sonner';

// Styles
import './App.css';
import '@blueprintjs/core/lib/css/blueprint.css';
import '@blueprintjs/datetime2/lib/css/blueprint-datetime2.css';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import '../styles/ag-grid-skyline.css';

// Focus management handled by base.css :focus-visible

/**
 * Root application component that sets up global providers and routing
 * 
 * Provides the following context layers:
 * - React Router for navigation
 * - React DnD for drag and drop functionality
 * - React Query for server state management
 * - Error boundary for graceful error handling
 * - Metadata provider for global CRM metadata
 * - Protected routes for authentication
 */
const App: React.FC = React.memo(() => {
  React.useEffect(() => {
    const root = document.documentElement;
    const usePointerFocus = () => {
      root.dataset.focusModality = 'pointer';
    };
    const useKeyboardFocus = () => {
      root.dataset.focusModality = 'keyboard';
    };

    document.addEventListener('pointerdown', usePointerFocus, true);
    document.addEventListener('keydown', useKeyboardFocus, true);
    return () => {
      document.removeEventListener('pointerdown', usePointerFocus, true);
      document.removeEventListener('keydown', useKeyboardFocus, true);
      delete root.dataset.focusModality;
    };
  }, []);

  return (
    <Router>
      <BuildUpdateWatcher />
      <DndProvider backend={HTML5Backend}>
        <QueryClientProvider client={queryClient}>
          <Toaster position="top-right" richColors closeButton duration={4000} />
          {/* Development tools - uncomment for debugging */}
          {/* <ReactQueryDevtools initialIsOpen={false} /> */}
          <ErrorBoundary>
            <Routes>
              {/* Protected application routes */}
              <Route 
                path="/*" 
                element={
                  <ProtectedRoute>
                    <MetadataProvider>
                      <AuthenticatedApp />
                    </MetadataProvider>
                  </ProtectedRoute>
                } 
              />
              
              {/* Authentication routes */}
              <Route path="/auth/login" element={<Login />} />
              <Route path="/auth/reset-password" element={<ResetPassword />} />
              <Route path="/auth/logout" element={<Logout />} />
            </Routes>
          </ErrorBoundary>
        </QueryClientProvider>
      </DndProvider>
    </Router>
  );
});

// Set display name for debugging
App.displayName = 'App';

export default App;
